// Pure handler for advancing to the next step, used by all success handlers in renderer
import React from 'react'

import { FormInstance } from 'rc-field-form'
import { AddToast } from 'react-toast-notifications'

import { IconEnum } from '@atoms/images'
import {
  CheckoutStepsEnum, CheckOutFlowTypeEnum, UserCheckoutStatus, UserSubscriptionFragment,
  CityDeliveryDaysEnum, MealKitCategoryFragment, UserCartQuery, UserDetailsQuery,
  OrderFragment,
} from '@hooks/api'
import { FieldData, RadioButtonProps } from '@molecules/inputs'
import { MealKitServingSizeEnum, NumberOfPortionsEnum } from '@uctypes/api/globalTypes'

// --- Pure functions, constants and types for Checkout Flow ----

export interface StepConfig {
  id: string | number
  label: string
  icon?: IconEnum
  /**
   * Optional: Declarative per-step icon config for upcoming/in-progress/completed states.
   */
  stepIcons?: {
    upcoming: IconEnum
    inProgress: IconEnum
    completed?: IconEnum
  }
}

export interface CheckoutStepperProps {
  steps: StepConfig[]
  currentStepIndex: number
  maxCompletedStepIndex: number
  onStepSelect: (stepIndex: number) => void // Will pass the index relative to the provided steps
  testModeEnabled?: boolean
  flowType: CheckOutFlowTypeEnum
  forceCompletedStepIndexes?: number[]
  categoryId?: string | null
}

export interface CheckoutStepRendererProps {
  currentStepObj: StepConfig
  state: {
    pageLoading: boolean
    numberOfPortions?: NumberOfPortionsEnum | null
    numberOfMeals?: number
    categoryId?: string | null
    orderData?: OrderFragment
    checkoutFlowType?: string
    currentStep?: number
    maxCompleted?: number
    showSuccessModal?: boolean
    selectedDeliveryDay?: CityDeliveryDaysEnum
  }
  form: FormInstance
  isMobile: boolean
  setState: React.Dispatch<React.SetStateAction<CheckoutFlowState>>
  isGuestUser: boolean
  checkoutStatus: UserCheckoutStatus
  handleOnCardClose: () => void
  handleChange: (changedFields: FieldData[]) => void
  handleConfirmDetails: () => Promise<void | OrderFragment>
  handleUpdateSubscription: () => void | Promise<void>
  handleGoToStepGuarded?: (stepIndex: number) => { allowed: boolean, reason?: string }
  subscription?: UserSubscriptionFragment
  actionTitle: string
}
export interface CheckoutFlowState {
  pageLoading: boolean
  categoryId: string | null
  currentCategoryId: string | null
  numberOfPortions: NumberOfPortionsEnum | null

  numberOfMeals: number | null
  selectedPlan: string | null
  activeTab: string
  currentStep: number
  maxCompleted: number
  checkoutFlowType: CheckOutFlowTypeEnum
  hasConfirmedAddress: boolean
  postConfirmLock?: boolean
}

export interface UseCheckoutFlowArgs {
  subscriptionSteps: StepConfig[]
  onDemandSteps: StepConfig[]
  form: FormInstance
  plansMealKitCategories: MealKitCategoryFragment[]
  checkoutStatus: UserCheckoutStatus
  userCartData: UserCartQuery
  userDetailsData: UserDetailsQuery
  addToast: (msg: string, opts?: { appearance?: string, autoDismiss?: boolean }) => void
  shouldPresentCheckoutUI?: boolean
  initialCheckoutFlowType?: string
}

export interface IconDetail {
  staticId: string
  title: string
  description: string
}

export interface PerDishPricingItem {
  servingSize: MealKitServingSizeEnum
  category: { id: string }
  price: number
}

export interface GetActionTitleArgs {
  flowType: CheckOutFlowTypeEnum
  currentStep: number
  currentSteps: StepConfig[]
  checkoutStatus: UserCheckoutStatus | undefined
}

// Helper to determine if checkout UI should be presented
export function getEffectiveShouldPresentCheckoutUI(
  flowType: CheckOutFlowTypeEnum,
  shouldPresentCheckoutUI: boolean | undefined,
  checkoutStatus: UserCheckoutStatus | undefined,
): boolean | undefined {
  if (flowType !== CheckOutFlowTypeEnum.SUBSCRIPTION) {
    return true
  }
  if (typeof shouldPresentCheckoutUI !== 'undefined') {
    return shouldPresentCheckoutUI
  }
  if (checkoutStatus && typeof checkoutStatus.shouldPresentCheckoutUI !== 'undefined') {
    return checkoutStatus.shouldPresentCheckoutUI
  }
  return undefined
}

/** True when UserCart has returned a checkoutStatus with an id (safe to read flow flags). */
export function isCheckoutStatusReady(
  checkoutStatus: UserCheckoutStatus | undefined | null,
): boolean {
  return !!checkoutStatus?.id
}

export function getCheckoutStatusFlowType(
  checkoutStatus: UserCheckoutStatus | undefined | null,
  fallback: CheckOutFlowTypeEnum,
): CheckOutFlowTypeEnum {
  return checkoutStatus?.checkoutFlowType ?? fallback
}

// Helper to safely get shouldPresentCheckoutUI from checkoutStatus
export function getSafeShouldPresentCheckoutUI(
  checkoutStatus: UserCheckoutStatus | undefined | null,
): boolean | undefined {
  return checkoutStatus && typeof checkoutStatus.shouldPresentCheckoutUI !== 'undefined'
    ? checkoutStatus.shouldPresentCheckoutUI
    : undefined
}

export const getSafeHasActiveSubscription = (
  checkoutStatus: UserCheckoutStatus | undefined | null,
): boolean | undefined =>
  checkoutStatus && typeof checkoutStatus.hasActiveSubscription !== 'undefined'
    ? checkoutStatus.hasActiveSubscription
    : false

export function getNextStepForHandler({
  state,
  currentSteps,
  checkoutStatus,
  checkoutLogger,
}: {
  state: CheckoutFlowState
  currentSteps: StepConfig[]
  checkoutStatus: UserCheckoutStatus
  checkoutLogger?: { debug?: (msg: string, obj?: unknown) => void, info?: (msg: string, obj?: unknown) => void }
}): number | null {
  const { addressStepIdx, paymentStepIdx, confirmDetailsIdx, lockedSummaryIdx, chooseAmountIdx, pickPlanIdx } = getStepIndexes(currentSteps)
  if (state.checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION) {
    return getSubscriptionNextStep({
      currentStep: state.currentStep,
      chooseAmountIdx,
      pickPlanIdx,
      addressStepIdx,
      paymentStepIdx,
      confirmDetailsIdx,
      lockedSummaryIdx,
      state,
      checkoutStatus,
      checkoutLogger,
    })
  } else if (state.checkoutFlowType === CheckOutFlowTypeEnum.ON_DEMAND) {
    return getOnDemandNextStep({
      currentStep: state.currentStep,
      addressStepIdx,
      paymentStepIdx,
      confirmDetailsIdx,
      checkoutStatus,
    })
  }
  return null
}

/**
 * Pure function to determine the next step for SUBSCRIPTION checkout flow.
 * Returns the step index to auto-advance to, or null if no auto-advance is needed.
 */
export function getSubscriptionNextStep({
  currentStep,
  chooseAmountIdx,
  pickPlanIdx,
  addressStepIdx,
  paymentStepIdx,
  confirmDetailsIdx,
  lockedSummaryIdx,
  state,
  checkoutStatus,
  checkoutLogger,
}: {
  currentStep: number
  chooseAmountIdx: number
  pickPlanIdx: number
  addressStepIdx: number
  paymentStepIdx: number
  confirmDetailsIdx: number
  lockedSummaryIdx: number
  state: CheckoutFlowState
  checkoutStatus: UserCheckoutStatus
  checkoutLogger?: { debug?: (msg: string, obj?: any) => void }
}): number | null {
  // Only log when a step transition is about to happen
  if (chooseAmountIdx !== -1 && (!state.numberOfPortions || !state.numberOfMeals)) {
    if (currentStep !== chooseAmountIdx) {
      checkoutLogger?.debug?.('[STEP_LOGIC][TO_CHOOSE_AMOUNT]', { currentStep })
      return chooseAmountIdx
    }
    return null
  }
  if (pickPlanIdx !== -1 && state.numberOfPortions && state.numberOfMeals && !state.categoryId) {
    if (currentStep !== pickPlanIdx) {
      checkoutLogger?.debug?.('[STEP_LOGIC][TO_PICK_PLAN]', { currentStep })
      return pickPlanIdx
    }
    return null
  }
  if (!checkoutStatus) return null
  if (addressStepIdx !== -1 && (!checkoutStatus.hasSetDeliveryAddress || !checkoutStatus.hasSetDeliveryAddressDetails)) {
    if (currentStep !== addressStepIdx) {
      checkoutLogger?.debug?.('[STEP_LOGIC][TO_ADDRESS]', { currentStep })
      return addressStepIdx
    }
    return null
  }
  if (paymentStepIdx !== -1 && checkoutStatus.hasSetDeliveryAddress && checkoutStatus.hasSetDeliveryAddressDetails && !checkoutStatus.hasSetPaymentMethod) {
    if (currentStep !== paymentStepIdx) {
      checkoutLogger?.debug?.('[STEP_LOGIC][TO_PAYMENT]', { currentStep })
      return paymentStepIdx
    }
    return null
  }
  if (confirmDetailsIdx !== -1 && checkoutStatus.hasSetPaymentMethod && !checkoutStatus.hasConfirmedCheckoutDetails) {
    if (currentStep !== confirmDetailsIdx) {
      checkoutLogger?.debug?.('[STEP_LOGIC][TO_CONFIRM]', { currentStep })
      return confirmDetailsIdx
    }
    return null
  }
  if (checkoutStatus.hasActiveSubscription || checkoutStatus.hasPausedSubscription) {
    if (currentStep !== lockedSummaryIdx) {
      checkoutLogger?.debug?.('[STEP_LOGIC][TO_SUMMARY]', { currentStep })
      return lockedSummaryIdx
    }
    return null
  }
  return null
}

export function getOnDemandNextStep({
  currentStep,
  addressStepIdx,
  paymentStepIdx,
  confirmDetailsIdx,
  checkoutStatus,
}: {
  currentStep: number
  addressStepIdx: number
  paymentStepIdx: number
  confirmDetailsIdx: number
  checkoutStatus: UserCheckoutStatus
}): number | null {
  // Terminal condition: if all steps are completed, always present Confirm page
  if (!checkoutStatus) return null
  if (
    checkoutStatus.hasSetDeliveryAddress &&
    checkoutStatus.hasSetDeliveryAddressDetails &&
    checkoutStatus.hasSetPaymentMethod &&
    checkoutStatus.hasConfirmedCheckoutDetails
  ) {
    if (currentStep !== confirmDetailsIdx) {
      return confirmDetailsIdx
    }
    return null
  }
  // If address is missing, lock to address step
  if (!checkoutStatus.hasSetDeliveryAddress || !checkoutStatus.hasSetDeliveryAddressDetails) {
    if (currentStep !== addressStepIdx) {
      return addressStepIdx
    }
    return null
  }
  // If payment is missing, lock to payment step
  if (!checkoutStatus.hasSetPaymentMethod) {
    if (currentStep !== paymentStepIdx) {
      return paymentStepIdx
    }
    return null
  }
  // If payment and address present but not confirmed, lock to confirm step
  if (
    checkoutStatus.hasSetDeliveryAddress &&
    checkoutStatus.hasSetDeliveryAddressDetails &&
    checkoutStatus.hasSetPaymentMethod &&
    !checkoutStatus.hasConfirmedCheckoutDetails
  ) {
    if (currentStep !== confirmDetailsIdx) {
      return confirmDetailsIdx
    }
    return null
  }
  return null
}

export const getStepIndexes = (currentSteps: StepConfig[]) => ({
  chooseAmountIdx: currentSteps.findIndex(s => s.id === CheckoutStepsEnum.CHOOSE_AMOUNT),
  pickPlanIdx: currentSteps.findIndex(s => s.id === CheckoutStepsEnum.PICK_PLAN),
  addressStepIdx: currentSteps.findIndex(s => s.id === CheckoutStepsEnum.ADD_ADDRESS),
  paymentStepIdx: currentSteps.findIndex(s => s.id === CheckoutStepsEnum.ADD_PAYMENT),
  confirmDetailsIdx: currentSteps.findIndex(s => s.id === CheckoutStepsEnum.CONFIRM_DETAILS),
  lockedSummaryIdx: 0,
})

// Returns the action button label for the current step, based on flow and status
export const getActionTitle = ({
  flowType,
  currentStep,
  currentSteps,
  checkoutStatus,
}: GetActionTitleArgs): string => {
  if (flowType === CheckOutFlowTypeEnum.ON_DEMAND && currentSteps[currentStep]?.id === CheckoutStepsEnum.CONFIRM_DETAILS) {
    return 'PLACE ORDER'
  }
  if (flowType === CheckOutFlowTypeEnum.SUBSCRIPTION && currentSteps[currentStep]?.id === CheckoutStepsEnum.CONFIRM_DETAILS) {
    return 'CONFIRM'
  }
  if (checkoutStatus?.hasActiveSubscription) {
    return 'UPDATE YOUR PLAN'
  }
  if (checkoutStatus?.hasPausedSubscription && checkoutStatus?.hasSetDeliveryAddress && checkoutStatus?.hasSetPaymentMethod) {
    return 'ACTIVATE PLAN'
  }
  return 'NEXT'
}
// Returns true if subscription state should be hydrated from API
export const shouldSyncSubscriptionFromApi = (
  checkoutStatus: UserCheckoutStatus | undefined,
  state: CheckoutFlowState,
  subscription: UserSubscriptionFragment | undefined,
): boolean => Boolean(
  checkoutStatus &&
  checkoutStatus.id &&
  state.checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION &&
  subscription &&
  checkoutStatus.hasSelectedPlan &&
  checkoutStatus.hasSelectedPortionSize &&
  (
    !state.categoryId ||
    !state.numberOfPortions ||
    !state.numberOfMeals
  ),
)

export const shouldLockSummaryPage = (
  checkoutStatus: UserCheckoutStatus | undefined,
  flowType: CheckOutFlowTypeEnum | undefined,
  hasLockedSummaryStep: boolean,
  shouldPresentCheckoutUI: boolean | undefined,
): boolean => {
  if (!checkoutStatus || !checkoutStatus.id) return false
  if (flowType !== CheckOutFlowTypeEnum.SUBSCRIPTION) return false
  if (hasLockedSummaryStep) return true
  if (shouldPresentCheckoutUI === false) return true
  return false
}

export const needsSubscriptionHydration = (state: CheckoutFlowState, subscription: UserSubscriptionFragment | undefined) => {
  if (!subscription) return false
  return (
    state.categoryId !== subscription.category?.id ||
    state.numberOfPortions !== subscription.numberOfPortions ||
    state.numberOfMeals !== subscription.numberOfMeals
  )
}

export const getHydratedSubscriptionValues = (subscription: UserSubscriptionFragment | undefined) => ({
  hydratedCategory: subscription?.category?.id ?? null,
  hydratedPeople: subscription?.numberOfPortions ?? null,
  hydratedDishes: subscription?.numberOfMeals ?? null,
})

export const clampStepIndex = (index: number, steps: StepConfig[]): number => Math.max(0, Math.min(index, steps.length - 1))

export const getStepName = (idx: number, steps: StepConfig[], prefix = '[CHECKOUT_DEBUG]'): string => {
  if (!steps || idx < 0 || idx >= steps.length) return `${prefix} [out-of-bounds]`
  return typeof steps[idx]?.id === 'string' ? steps[idx]?.id : `${prefix} [unknown]`
}

// Step progression helpers: determine if a step is required based on status and step index
export const needsAddressStep = (status: UserCheckoutStatus, addressIdx: number) =>
  addressIdx !== -1 && (!status.hasSetDeliveryAddress || !status.hasSetDeliveryAddressDetails)

export const needsPaymentStep = (status: UserCheckoutStatus, paymentIdx: number) =>
  paymentIdx !== -1 && status.hasSetDeliveryAddress && status.hasSetDeliveryAddressDetails && !status.hasSetPaymentMethod

export const needsConfirmStep = (status: UserCheckoutStatus, confirmIdx: number) =>
  confirmIdx !== -1 && status.hasSetPaymentMethod && !status.hasConfirmedCheckoutDetails

export const isOrderConfirmedOrUILocked = (status: UserCheckoutStatus) =>
  status && (status.hasConfirmedCheckoutDetails || status.shouldPresentCheckoutUI === false)

// Returns the last completed checkout step from API status
export const getDbLastCheckoutStep = (checkoutStatus: UserCheckoutStatus | undefined): CheckoutStepsEnum | undefined => {
  if (!checkoutStatus) return undefined
  if (checkoutStatus.hasSetPaymentMethod) return CheckoutStepsEnum.ADD_PAYMENT
  if (checkoutStatus.hasSetDeliveryAddressDetails || checkoutStatus.hasSetDeliveryAddress) return CheckoutStepsEnum.ADD_ADDRESS
  return undefined
}

// Returns indexes of steps that are restricted for guest users
export const getRestrictedStepIndexes = (indexes: { addressStepIdx: number, paymentStepIdx: number, confirmDetailsIdx: number }) =>
  [indexes.addressStepIdx, indexes.paymentStepIdx, indexes.confirmDetailsIdx].filter(idx => idx !== -1)

export function getHydratedPlan(subscription?: UserSubscriptionFragment) {
  return {
    numberOfPortions: subscription?.numberOfPortions ?? null,
    numberOfMeals: subscription?.numberOfMeals ?? null,
    categoryId: subscription?.category?.id ?? null,
  }
}

export function getAddressNextStepIndex(checkoutFlowType: string): number {
  return checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION ? 2 : 1
}
export function getPaymentNextStepIndex(checkoutFlowType: string): number {
  return checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION ? 4 : 1
}

export function shouldDisplayConfirmation(hasUserEnteredAddressFromModal: boolean, checkoutFlowType: string): boolean {
  return hasUserEnteredAddressFromModal && checkoutFlowType === CheckOutFlowTypeEnum.ON_DEMAND
}
export function isConfirmDetailsButtonDisabled(
  pageLoading: boolean,
  checkoutFlowType: string,
  selectedDeliveryDay: CityDeliveryDaysEnum | undefined,
): boolean {
  return (
    pageLoading ||
    (checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION && !selectedDeliveryDay)
  )
}
export function shouldHideSubscriptionStep(
  checkoutFlowType: string,
  shouldPresentCheckoutUI: boolean,
  currentStepId: string | number,
): boolean {
  return (
    checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION &&
    shouldPresentCheckoutUI === false &&
    currentStepId !== CheckoutStepsEnum.CHOOSE_AMOUNT &&
    currentStepId !== CheckoutStepsEnum.PICK_PLAN
  )
}

export function shouldForceChooseAmountStep(
  checkoutFlowType: string,
  shouldPresentCheckoutUI: boolean,
  currentStep: number,
  maxCompleted: number,
): boolean {
  return (
    checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION &&
    shouldPresentCheckoutUI === false &&
    (currentStep !== 0 || maxCompleted !== -1)
  )
}

export function shouldBlockOnDemandGuestStep(
  checkoutStatus: UserCheckoutStatus | undefined | null,
  currentStep: number,
  hasShownGuestBlock: boolean,
  handleGoToStepGuarded?: (stepIndex: number) => { allowed: boolean, reason?: string },
): boolean {
  if (!isCheckoutStatusReady(checkoutStatus)) {
    return false
  }
  return (
    checkoutStatus?.checkoutFlowType === CheckOutFlowTypeEnum.ON_DEMAND &&
    !checkoutStatus?.hasAccount &&
    currentStep === 0 &&
    !!handleGoToStepGuarded &&
    !hasShownGuestBlock
  )
}

export const getPeopleRadioProps = (): RadioButtonProps => ({
  name: 'peopleRadio',
  options: [
    { value: NumberOfPortionsEnum.SERVES_1, element: React.createElement('span', null, '1 person') },
    { value: NumberOfPortionsEnum.SERVES_2, element: React.createElement('span', null, '2 people') },
    { value: NumberOfPortionsEnum.SERVES_3, element: React.createElement('span', null, '3 people') },
    { value: NumberOfPortionsEnum.SERVES_4, element: React.createElement('span', null, '4 people') },
  ],
  variant: 'horizontal',
  error: '',
  showLabel: false,
  showOptional: false,
  componentTheme: 'orange',
  rules: [{ required: true, message: 'Please select a value' }],
})

export const getDishesRadioProps = (addToast: AddToast): RadioButtonProps => ({
  name: 'dishesRadio',
  options: [
    { value: 2, element: React.createElement('span', null, '2 dishes') },
    { value: 3, element: React.createElement('span', null, '3 dishes') },
    { value: 4, element: React.createElement('span', null, '4 dishes') },
  ],
  variant: 'horizontal',
  error: '',
  showLabel: false,
  showOptional: false,
  componentTheme: 'orange',
  rules: [{ required: true, message: 'Please select a value' }],
  beforeChange: (form: FormInstance) => {
    return form.getFieldValue('peopleRadio') !== undefined && form.getFieldValue('peopleRadio') !== null
  },
  onPreventChange: () => {
    addToast('Please select the number of people before choosing dishes.', { appearance: 'error', autoDismiss: true })
  },
})

export const getCategoryRadioProps = (
  categoryOptions: RadioButtonProps['options'],
  state: { categoryId?: string | null },
): RadioButtonProps => ({
  name: 'categoryRadio',
  options: categoryOptions,
  value: state.categoryId ?? undefined,
  variant: 'horizontal',
  error: '',
  componentTheme: 'orange',
  showLabel: false,
  showOptional: false,
  rules: [{ required: true, message: 'Please select a value' }],
  className: 'category-radio',
})

export const iconDetails: IconDetail[] = [
  { staticId: 'you-choose', title: 'Choose your plan', description: 'Pick the category that fits your lifestyle, the number of people you\'re cooking for, and how many dishes you want each week.' },
  { staticId: 'we-deliver', title: 'We deliver', description: 'Receive perfectly pre-portioned ingredients and easy recipes delivered to your door weekly.' },
  { staticId: 'you-cook', title: 'Flexibility is yours', description: 'Swap dishes, update your plan, or pause whenever you like. No strings attached.' },
]

export const getPresetPrice = (
  numberOfMeals: number,
  portions: NumberOfPortionsEnum,
  categoryId: string,
  perDishPricingDataArray: PerDishPricingItem[] | undefined,
  fixedFee: number | undefined,
): string => {
  let mealKitSelectedSize: MealKitServingSizeEnum | undefined
  if (portions === NumberOfPortionsEnum.SERVES_1) mealKitSelectedSize = MealKitServingSizeEnum.SERVES_1
  else if (portions === NumberOfPortionsEnum.SERVES_2) mealKitSelectedSize = MealKitServingSizeEnum.SERVES_2
  else if (portions === NumberOfPortionsEnum.SERVES_3) mealKitSelectedSize = MealKitServingSizeEnum.SERVES_3
  else if (portions === NumberOfPortionsEnum.SERVES_4) mealKitSelectedSize = MealKitServingSizeEnum.SERVES_4
  const matchingPrice = perDishPricingDataArray?.find((item) => item.servingSize === mealKitSelectedSize && item.category.id === categoryId)
  const priceBeforeFee = matchingPrice ? matchingPrice.price * numberOfMeals : 0
  const totalPrice = priceBeforeFee + (fixedFee ?? 0)
  return 'R' + String(totalPrice)
}

export const isPlanChangedUtil = (
  orig: { numberOfPortions: NumberOfPortionsEnum | null | undefined, numberOfMeals: number | null | undefined, categoryId: string | null | undefined },
  people: NumberOfPortionsEnum | null | undefined,
  dishes: number | null | undefined,
  category: string | null | undefined,
): boolean => {
  return people !== orig.numberOfPortions || dishes !== orig.numberOfMeals || category !== orig.categoryId
}

export const getIsPlansNextButtonDisabled = (
  shouldPresentCheckoutUI: boolean,
  hasActiveSubscription: boolean,
  isPlanChanged: boolean,
  pageLoading: boolean,
  form: FormInstance,
): boolean => {
  if (!shouldPresentCheckoutUI && hasActiveSubscription) {
    if (!form.getFieldValue('peopleRadio') || !form.getFieldValue('dishesRadio') || !form.getFieldValue('categoryRadio') || pageLoading) {
      return true
    }
    return !isPlanChanged
  }
  if (!form.getFieldValue('peopleRadio') || !form.getFieldValue('dishesRadio') || !form.getFieldValue('categoryRadio') || pageLoading) {
    return true
  }
  if (!shouldPresentCheckoutUI) {
    return pageLoading
  }
  return false
}

// Pure step definitions
export const getSubscriptionSteps = () => [
  { id: CheckoutStepsEnum.CHOOSE_AMOUNT, label: 'How many servings and how often?' },
  { id: CheckoutStepsEnum.PICK_PLAN, label: 'Pick a Plan' },
  { id: CheckoutStepsEnum.ADD_ADDRESS, label: 'Add Delivery Address' },
  { id: CheckoutStepsEnum.ADD_PAYMENT, label: 'Add Payment Details' },
  { id: CheckoutStepsEnum.CONFIRM_DETAILS, label: 'Confirm Details' },
  {
    id: CheckoutStepsEnum.SELECT_MEAL_KITS,
    label: 'Select Meal Kits',
    stepIcons: {
      upcoming: 'SELECT_MEAL_KITS_UPCOMING',
      inProgress: 'SELECT_MEAL_KITS_IN_PROGRESS',
      completed: 'SELECT_MEAL_KITS_COMPLETED',
    },
  },
]
export const getOnDemandSteps = () => [
  { id: CheckoutStepsEnum.ADD_ADDRESS, label: 'Add Delivery Address' },
  { id: CheckoutStepsEnum.ADD_PAYMENT, label: 'Add Payment Details' },
  { id: CheckoutStepsEnum.CONFIRM_DETAILS, label: 'Confirm Details' },
]
export const shouldRenderCheckoutStep = (
  displayedStep: number,
  currentSteps: StepConfig[],
  state: CheckoutFlowState,
  shouldPresentCheckoutUI: boolean,
  checkoutStatus: UserCheckoutStatus | undefined | null,
) => (
  isCheckoutStatusReady(checkoutStatus) &&
  displayedStep >= 0 &&
  displayedStep < currentSteps.length &&
  (
    state.checkoutFlowType === CheckOutFlowTypeEnum.ON_DEMAND ||
    shouldPresentCheckoutUI ||
    currentSteps[displayedStep]?.id === CheckoutStepsEnum.CHOOSE_AMOUNT ||
    currentSteps[displayedStep]?.id === CheckoutStepsEnum.PICK_PLAN
  ) &&
  !(
    state.checkoutFlowType === CheckOutFlowTypeEnum.SUBSCRIPTION &&
    checkoutStatus?.shouldPresentCheckoutUI === false &&
    currentSteps[displayedStep]?.id !== CheckoutStepsEnum.CHOOSE_AMOUNT &&
    currentSteps[displayedStep]?.id !== CheckoutStepsEnum.PICK_PLAN
  )
)
